import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText, { FormLabel } from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import TimeInput from "../../components/Core/TimeInput/TimeInput";
import Flex from "../../components/Layout/Flex";
import InfoBlock from "../../components/Shared/InfoBlock/InfoBlock";
import TimeReportHistory from "../../components/Shared/TimeReportHistory/TimeReportHistory";
import { MAX_BREAK_HOURS } from "../../configs/app.configs";
import { ColorType } from "../../configs/constants/ColorType";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { routes } from "../../configs/constants/routes";
import { TIME_REPORT_STATUS } from "../../configs/constants/TimeReportStatus";
import useEscape from "../../hooks/useEscape";
import useOutsideAlerter from "../../hooks/useOutsideAlerter";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import TimeReportModel, { Reported, UpdateTimeReportRequest } from "../../models/TimeReport";
import {
	usePublishTimeReports,
	useTimeReport,
	useUpdateTimeReport,
} from "../../networking/time-report.hooks";
import { appTheme } from "../../theme/Theme";
import { contactTimeRange, isFuture } from "../../utils/date.time.utils";
import { formatRoute, jobRoute } from "../../utils/route.utils";
import { formatToMaxTwoDecimals } from "../../utils/number.utils";
import { toast } from "../../utils/toast";
import { useSplitViewStore } from "../../stores/splitView.store";

/**
 * Fetches the active time report (id set on splitViewStore by the list page)
 * and mounts the form fresh per report — the form's state initializers read
 * from the report, so it must not render before data exists, and it must
 * remount when the id changes (previously guaranteed by the store's
 * splitViewDataLoading gate in SplitView).
 */
const TimeReportDetailsSideBar: React.FC = () => {
	const splitViewStore = useSplitViewStore();
	const { timeReport, isLoading } = useTimeReport(splitViewStore.activeTimeReportId);

	if (isLoading || !timeReport) {
		return <LoadingIndicator />;
	}

	return <TimeReportDetails key={timeReport.id} timeReport={timeReport} />;
};

const TimeReportDetails: React.FC<{ timeReport: TimeReportModel }> = ({ timeReport }) => {
	const splitViewStore = useSplitViewStore();
	const { mutateAsync: updateTimeReportMutation } = useUpdateTimeReport();
	const { mutateAsync: publishTimeReportsMutation } = usePublishTimeReports();
	const [updating, setUpdating] = useState(false);

	const { t } = useTranslation();

	type ReportTimeActions = "ReportedStartTime" | "ReportedEndTime";
	type BillTimeActions = "BillStartTime" | "BillEndTime";
	type UpdateTimeReportActions = "UpdateReportedTime" | "UpdateBillTime" | "UpdateDeduction";

	const [reportedStartTime, setReportedStartTime] = useState<string | undefined>(
		moment(timeReport?.reported.start).format(DATE_TIME_FORMAT_TYPES.TIME)
	);

	const [reportedEndTime, setReportedEndTime] = useState<string | undefined>(
		moment(timeReport?.reported.end).format(DATE_TIME_FORMAT_TYPES.TIME)
	);

	const [showReportedTimeSaveButton, setShowReportedTimeSaveButton] = useState<boolean>(false);

	const [showBillTimeSaveButton, setShowBillTimeSaveButton] = useState<boolean>(false);

	const [showFoodDeductionSaveButton, setShowFoodDeductionSaveButton] = useState<boolean>(false);

	const [isBillStartTimePlaceHolder, setBillStartTimePlaceHolder] = useState<boolean>(false);
	const [isBillEndTimePlaceHolder, setBillEndTimePlaceHolder] = useState<boolean>(false);

	const [isReportStartTimePlaceHolder, setReportStartTimePlaceHolder] = useState<boolean>(false);
	const [isReportEndTimePlaceHolder, setReportEndTimePlaceHolder] = useState<boolean>(false);

	useEffect(() => {
		const isPending = timeReport?.status === TIME_REPORT_STATUS.PENDING;

		setReportStartTimePlaceHolder(isPending);
		setReportEndTimePlaceHolder(isPending);

		setBillStartTimePlaceHolder(!timeReport?.invoiceOverride?.start);
		setBillEndTimePlaceHolder(!timeReport?.invoiceOverride?.end);
	}, [timeReport]);

	const startTimeInputRef = useRef(null);
	useOutsideAlerter(startTimeInputRef, () => setShowReportedTimeSaveButton(false));

	const billTimeInputRef = useRef(null);
	useOutsideAlerter(billTimeInputRef, () => setShowBillTimeSaveButton(false));

	const foodDeductionInputRef = useRef(null);
	useOutsideAlerter(foodDeductionInputRef, () => setShowFoodDeductionSaveButton(false));

	const [billStartTime, setBillStartTime] = useState<string | undefined>(
		moment(timeReport?.invoiceOverride?.start || timeReport?.reported?.start).format(
			DATE_TIME_FORMAT_TYPES.TIME
		)
	);

	const [billEndTime, setBillEndTime] = useState<string | undefined>(
		moment(timeReport?.invoiceOverride?.end || timeReport?.reported?.end).format(
			DATE_TIME_FORMAT_TYPES.TIME
		)
	);

	const [reportedBreakHours, setReportedBreakHours] = useState<number>(
		getDefaultBreakHours(timeReport?.reported, timeReport?.job?.customer.breakTime)
	);

	const [billedBreakHours, setBilledBreakHours] = useState<number>(
		getDefaultBreakHours(
			timeReport?.invoiceOverride || timeReport?.reported,
			timeReport?.job?.customer.breakTime
		)
	);

	const [deduction, setFoodDeduction] = useState<number>(timeReport?.reported?.deduction || 0);

	const handelReportedBreakTime = (e: React.FormEvent<HTMLInputElement>) => {
		setReportedBreakHours(+e.currentTarget.value);
	};

	const handleBilledBreakHours = (e: React.FormEvent<HTMLInputElement>) => {
		setBilledBreakHours(+e.currentTarget.value);
	};

	const handleChangeDeduction = (value: number) => {
		setFoodDeduction(Math.abs(value));
		setShowFoodDeductionSaveButton(true);
	};

	useEscape({ onEscape: splitViewStore.hideSplitView });

	const updateTimeReports = async (action: UpdateTimeReportActions) => {
		if (timeReport?.status && timeReport.id) {
			let updateTimeReportRequest: UpdateTimeReportRequest = {};

			// Note: both start and end are anchored on the reported START date
			// (with midnight wrap) - see contactTimeRange for why anchoring the
			// end on the old end date corrupts midnight-ending reports.
			switch (action) {
				case "UpdateReportedTime": {
					const { start, end } = contactTimeRange(
						timeReport.reported.start,
						reportedStartTime as string,
						reportedEndTime as string
					);
					updateTimeReportRequest = {
						reported: {
							...timeReport.reported,
							start,
							end,
							break: reportedBreakHours,
						},
					};
					break;
				}
				case "UpdateBillTime":
					if (billStartTime && billEndTime) {
						const { start, end } = contactTimeRange(
							timeReport.reported.start,
							billStartTime,
							billEndTime
						);
						updateTimeReportRequest = {
							invoiceOverride: {
								start,
								end,
								break: billedBreakHours,
							},
						};
					}
					break;
				case "UpdateDeduction": {
					const { start, end } = contactTimeRange(
						timeReport.reported.start,
						reportedStartTime as string,
						reportedEndTime as string
					);
					updateTimeReportRequest = {
						reported: {
							...timeReport.reported,
							start,
							end,
							deduction: deduction,
						},
					};
					break;
				}
			}

			setUpdating(true);
			const result = await updateTimeReportMutation({
				timeReportId: timeReport.id,
				update: updateTimeReportRequest,
			})
				.then(() => true)
				.catch(() => false);
			setUpdating(false);

			if (result) {
				toast.success(t("timeReports.updateSuccess"));
				setShowReportedTimeSaveButton(false);
				setShowBillTimeSaveButton(false);
				setShowFoodDeductionSaveButton(false);
			} else toast.error(t("SOMETHING_WENT_WRONG"));
		}
	};

	const [initFirstRender, setInitFirstRender] = useState<boolean>(false);

	type FOCUS_EVENTS = "ReportedTimeFocusEvent" | "BillTimeFocusEvent" | "DeductionFocusEvent";

	const handleOnFocusEventHandler = (
		focusEvent: FOCUS_EVENTS,
		action?: BillTimeActions | ReportTimeActions
	) => {
		switch (focusEvent) {
			case "ReportedTimeFocusEvent":
				setShowReportedTimeSaveButton(true);
				if (action === "ReportedStartTime") setReportStartTimePlaceHolder(false);
				if (action === "ReportedEndTime") setReportEndTimePlaceHolder(false);
				break;
			case "BillTimeFocusEvent":
				setShowBillTimeSaveButton(true);
				if (action === "BillStartTime") setBillStartTimePlaceHolder(false);
				if (action === "BillEndTime") setBillEndTimePlaceHolder(false);
				break;
			case "DeductionFocusEvent":
				setShowFoodDeductionSaveButton(true);
				break;
		}

		setInitFirstRender(true);
	};

	const handleReportTimeOnChange = (time: string, action: ReportTimeActions) => {
		switch (action) {
			case "ReportedStartTime":
				setReportedStartTime(time);
				break;
			case "ReportedEndTime":
				setReportedEndTime(time);
				break;
		}
		if (initFirstRender) setShowReportedTimeSaveButton(true);
	};

	const handleBillTimeOnChange = (time: string, action: BillTimeActions) => {
		switch (action) {
			case "BillStartTime":
				setBillStartTime(time);
				break;
			case "BillEndTime":
				setBillEndTime(time);
				break;
		}

		if (initFirstRender) setShowBillTimeSaveButton(true);
	};

	const updateTimeReportStatus = async ({
		status,
		publishToCustomer,
	}: {
		status?: TIME_REPORT_STATUS;
		publishToCustomer?: boolean;
	}) => {
		if (!timeReport) return;

		if (publishToCustomer) {
			const result = await publishTimeReportsMutation([timeReport.id])
				.then(() => true)
				.catch(() => false);
			toast.genericSuccessOrError(result);
		}

		if (status) {
			const result = await updateTimeReportMutation({
				timeReportId: timeReport.id,
				update: { status },
			})
				.then(() => true)
				.catch(() => false);

			toast.genericSuccessOrError(result);
		}
	};

	const isFutureTimeReport = timeReport ? isFuture(timeReport?.shift.start) : true;

	const showTime = (hourLabel: string, minutesLabel: string, time?: number): string => {
		if (time) {
			if (time > 60) {
				const hours = Math.floor(time / 60);
				const minutes = time - hours * 60;

				return `${hours}:${minutes < 9 ? "0" + minutes : minutes} ${hourLabel}`;
			} else return `${time} ${minutesLabel}`;
		}

		return `0 ${minutesLabel}`;
	};

	const formatDiffColor = (billedDiff?: number): ColorType => {
		if (billedDiff)
			if (billedDiff > 0) return "black";
			else return "danger";

		return "grayText";
	};

	return (
		<TimeReportDetailsSideBarStyles>
			<Link
				to={formatRoute(routes.CONSULTANTS_DETAILS + routes.CONSULTANTS_PROFILE, [
					timeReport?.consultant?.id || "",
				])}
				target="_blank"
				data-contact={timeReport?.consultant?.id}
			>
				<AppText size="xm" weight="bold" color="grayText">
					{timeReport?.consultant?.firstName} {timeReport?.consultant?.lastName} (#
					{timeReport?.consultant?.profile?.employeeNumber})
				</AppText>
			</Link>

			<Flex margin="1rem 0" justifyContent="space-between">
				<InfoBlock
					label="CUSTOMER"
					value={timeReport?.job?.customer?.name}
					link={formatRoute(routes.CUSTOMER_DETAILS, [timeReport?.job?.customer.id])}
				/>
				<InfoBlock
					label="JOB"
					value={timeReport?.job?.orderNumber}
					link={jobRoute(timeReport?.job?.id || "")}
					width="80px"
				/>
			</Flex>
			<Flex justifyContent="space-between">
				<InfoBlock
					label="PLANNED_TIME"
					ignoreTranslation
					value={`${moment(timeReport?.shift?.start).format(
						DATE_TIME_FORMAT_TYPES.TIME
					)} - ${moment(timeReport?.shift?.end).format(DATE_TIME_FORMAT_TYPES.TIME)}`}
				/>
				<InfoBlock
					label="DATE"
					value={moment(timeReport?.shift?.start).format(DATE_TIME_FORMAT_TYPES.DATE)}
					width="80px"
				/>
			</Flex>

			{!!timeReport?.absence && (
				<Flex margin="1rem 0">
					<InfoBlock
						label="absence.reason"
						value={t(`absence.type.${timeReport?.absence?.type}`) + ""}
					/>
				</Flex>
			)}
			<>
				<Flex margin="1rem 0">
					<Flex direction="column">
						<FormLabel margin="0.5rem 0" tooltip={t("timeReports.reportedTimeHelp")}>
							{t("REPORTED_TIME")}
						</FormLabel>
						<Flex alignItems="center">
							<Flex alignItems="center">
								<TimeInput
									inputRef={startTimeInputRef}
									onFocus={() =>
										handleOnFocusEventHandler("ReportedTimeFocusEvent", "ReportedStartTime")
									}
									onClick={() =>
										handleOnFocusEventHandler("ReportedTimeFocusEvent", "ReportedStartTime")
									}
									selectedTime={reportedStartTime}
									onChange={(time: string) => handleReportTimeOnChange(time, "ReportedStartTime")}
									disabled={isFutureTimeReport}
									onEnter={() => updateTimeReports("UpdateReportedTime")}
									textColor={
										isReportStartTimePlaceHolder ? appTheme.colors.PLACEHOLDER_TEXT : "black"
									}
								/>
								<AppText margin="0 0.5rem">-</AppText>
								<TimeInput
									inputRef={startTimeInputRef}
									onFocus={() =>
										handleOnFocusEventHandler("ReportedTimeFocusEvent", "ReportedEndTime")
									}
									onClick={() =>
										handleOnFocusEventHandler("ReportedTimeFocusEvent", "ReportedEndTime")
									}
									selectedTime={reportedEndTime}
									onChange={(time: string) => handleReportTimeOnChange(time, "ReportedEndTime")}
									disabled={isFutureTimeReport}
									onEnter={() => updateTimeReports("UpdateReportedTime")}
									textColor={
										isReportEndTimePlaceHolder ? appTheme.colors.PLACEHOLDER_TEXT : "black"
									}
								/>
							</Flex>
						</Flex>
					</Flex>

					<Flex direction="column">
						<FormLabel margin="0.5rem">{t("BREAK")}</FormLabel>
						<Flex alignItems="center">
							<AppInput
								onChange={(e: React.FormEvent<HTMLInputElement>) => handelReportedBreakTime(e)}
								name="reported.break"
								value={reportedBreakHours}
								min={0}
								max={MAX_BREAK_HOURS}
								containerRef={startTimeInputRef}
								onFocus={() => handleOnFocusEventHandler("ReportedTimeFocusEvent")}
								onClick={() => handleOnFocusEventHandler("ReportedTimeFocusEvent")}
								type="number"
								containerMargin="0 0.5rem"
								containerWidth="4.375rem"
								disabled={isFutureTimeReport}
								// selectOnFocus
								onEnter={() => updateTimeReports("UpdateReportedTime")}
							/>
							{showReportedTimeSaveButton && !isFutureTimeReport && (
								<Button
									inputRef={startTimeInputRef}
									onClick={() => updateTimeReports("UpdateReportedTime")}
									height="2.0625rem"
									backgroundColor="primary"
									icon="white-check"
									width="2.8rem"
									margin="0 0 0.5rem"
									loading={updating}
								/>
							)}
						</Flex>
					</Flex>
				</Flex>

				<Flex margin="1rem 0">
					<Flex direction="column">
						<FormLabel margin="0.5rem 0" tooltip={t("timeReports.billedTimeHelp")}>
							{t("BILLED_TIME")}
						</FormLabel>
						<Flex alignItems="center">
							<Flex alignItems="center">
								<TimeInput
									textColor={
										isBillStartTimePlaceHolder ? appTheme.colors.PLACEHOLDER_TEXT : "black"
									}
									inputRef={billTimeInputRef}
									onFocus={() => handleOnFocusEventHandler("BillTimeFocusEvent", "BillStartTime")}
									onClick={() => handleOnFocusEventHandler("BillTimeFocusEvent", "BillStartTime")}
									selectedTime={billStartTime}
									onChange={(time: string) => handleBillTimeOnChange(time, "BillStartTime")}
									disabled={isFutureTimeReport}
									onEnter={() => updateTimeReports("UpdateBillTime")}
								/>
								<AppText margin="0 0.5rem">-</AppText>
								<TimeInput
									textColor={isBillEndTimePlaceHolder ? appTheme.colors.PLACEHOLDER_TEXT : "black"}
									inputRef={billTimeInputRef}
									onFocus={() => handleOnFocusEventHandler("BillTimeFocusEvent", "BillEndTime")}
									onClick={() => handleOnFocusEventHandler("BillTimeFocusEvent", "BillEndTime")}
									selectedTime={billEndTime}
									onChange={(time: string) => handleBillTimeOnChange(time, "BillEndTime")}
									disabled={isFutureTimeReport}
									onEnter={() => updateTimeReports("UpdateBillTime")}
								/>
							</Flex>
						</Flex>
					</Flex>

					<Flex direction="column">
						<FormLabel margin="0.5rem">{t("BREAK")}</FormLabel>
						<Flex alignItems="center">
							<AppInput
								onChange={(e: React.FormEvent<HTMLInputElement>) => handleBilledBreakHours(e)}
								name="invoiceOverride.break"
								placeholder="0"
								value={billedBreakHours}
								// value={
								// 	timeReport?.invoiceOverride?.break
								// 		? billedBreakHours
								// 		: timeReport?.reported.break
								// }
								min={0}
								max={MAX_BREAK_HOURS}
								containerRef={billTimeInputRef}
								onFocus={() => handleOnFocusEventHandler("BillTimeFocusEvent")}
								onClick={() => handleOnFocusEventHandler("BillTimeFocusEvent")}
								type="number"
								containerMargin="0 0.5rem"
								containerWidth="4.375rem"
								disabled={isFutureTimeReport}
								onEnter={() => updateTimeReports("UpdateBillTime")}
							/>
							{showBillTimeSaveButton && !isFutureTimeReport && (
								<Button
									inputRef={billTimeInputRef}
									onClick={() => updateTimeReports("UpdateBillTime")}
									height="2.0625rem"
									backgroundColor="primary"
									icon="white-check"
									width="2.875rem"
									margin="0 0 0.5rem"
									loading={updating}
								/>
							)}
						</Flex>
					</Flex>
				</Flex>

				<Flex margin="1rem 0">
					<Flex direction="column">
						<FormLabel margin="0.5rem 0">{t("DEDUCTION")}</FormLabel>
						<Flex alignItems="center">
							<AppInput
								onChangeNumber={handleChangeDeduction}
								name="reported.deduction"
								placeholder="0"
								value={deduction}
								containerRef={foodDeductionInputRef}
								onFocus={() => handleOnFocusEventHandler("DeductionFocusEvent")}
								type="number"
								containerMargin="0"
								containerWidth="5rem"
								// selectOnFocus
								disabled={isFutureTimeReport}
								min={0}
								onEnter={() => updateTimeReports("UpdateDeduction")}
							/>
							{showFoodDeductionSaveButton && (
								<Button
									inputRef={foodDeductionInputRef}
									onClick={() => updateTimeReports("UpdateDeduction")}
									height="2.0625rem"
									backgroundColor="primary"
									icon="white-check"
									width="2.875rem"
									margin="0 0 0.5rem 0.5rem"
								/>
							)}
						</Flex>
					</Flex>
				</Flex>

				<hr />

				{timeReport && (
					<>
						<Flex margin="1rem 0 0.5rem 0">
							<AppText size="m">
								{showTime("", "min", timeReport.invoicedTime)} {t("timeReports.billableTime")},
							</AppText>
							<AppText color={formatDiffColor(timeReport.billedDiff)} margin="0 0.5rem" size="m">
								{showTime("h diff", "min diff", timeReport.billedDiff)}
							</AppText>
						</Flex>
						{timeReport.extraTimeOrderedBy && (
							<AppText size="m" color={"grayText"}>
								{`${timeReport.extraTimeOrderedBy} bad konsulten att arbeta längre`}
							</AppText>
						)}
					</>
				)}

				{timeReport?.declineComment && (
					<Flex margin="1.5rem 0 2rem 0" direction="column">
						<FormLabel color="danger">{t("DECLINED_REASON")}</FormLabel>
						<AppText italic color="danger">
							{timeReport?.declineComment}
						</AppText>
					</Flex>
				)}

				{timeReport && (
					<Flex direction="column" margin="1rem 0 0 0" padding="0 10px 0 0">
						<TimeReportHistory timeReport={timeReport} onAction={updateTimeReportStatus} />
					</Flex>
				)}
			</>
		</TimeReportDetailsSideBarStyles>
	);
};

export default TimeReportDetailsSideBar;

const TimeReportDetailsSideBarStyles = styled.div`
	overflow-y: auto;
	height: 90vh;
`;

const getDefaultBreakHours = (report?: Reported, customerBreakTime?: number) => {
	if (!report) {
		return 0;
	} else if (report.break !== undefined) {
		return formatToMaxTwoDecimals(report.break);
	} else if (moment(report.end).diff(report.start, "hours") >= 5) {
		// Note: As per Swedish law, shift with eq or more than 5 hours should have 30 min break
		// return 0.5;
		return customerBreakTime && customerBreakTime > 0.5 ? customerBreakTime : 0.5;
	} else {
		return 0;
	}
};
